import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between h-[70px] px-6 md:px-10">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex flex-col">
            <span className="text-foreground-900 font-heading text-lg font-semibold tracking-tight leading-tight">
              gtbuyspreadsheet
            </span>
            <span className="text-foreground-500 text-[11px] tracking-widest uppercase">
              Verified Hub
            </span>
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className="text-foreground-700 text-sm font-medium hover:text-primary-500 transition-colors whitespace-nowrap">
            <i className="ri-home-4-line mr-1.5"></i>Home
          </Link>
          <span className="text-foreground-300 text-[10px]">&diams;</span>
          <a href="#categories" className="text-foreground-700 text-sm font-medium hover:text-primary-500 transition-colors whitespace-nowrap">
            Categories
          </a>
          <span className="text-foreground-300 text-[10px]">&diams;</span>
          <a href="#guides" className="text-foreground-700 text-sm font-medium hover:text-primary-500 transition-colors whitespace-nowrap">
            Guides
          </a>
          <span className="text-foreground-300 text-[10px]">&diams;</span>
          <Link to="/spreadsheet-beginners-guide" className="text-foreground-700 text-sm font-medium hover:text-primary-500 transition-colors whitespace-nowrap">
            Shopping Guide
          </Link>
          <span className="text-foreground-300 text-[10px]">&diams;</span>
          <a href="#about" className="text-foreground-700 text-sm font-medium hover:text-primary-500 transition-colors whitespace-nowrap">
            About
          </a>
          <span className="text-foreground-300 text-[10px]">&diams;</span>
          <a href="#safety" className="text-foreground-700 text-sm font-medium hover:text-primary-500 transition-colors whitespace-nowrap">
            Safety
          </a>
        </div>

        <div className="flex items-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Main Store
            <span className="w-5 h-5 flex items-center justify-center bg-background-50/20 rounded-full">
              <i className="ri-arrow-right-up-line text-xs"></i>
            </span>
          </a>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-6 py-5 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-foreground-700 text-sm font-medium py-2.5 border-b border-background-100">
            <i className="ri-home-4-line"></i> Home
          </Link>
          <a href="#categories" onClick={() => setMobileOpen(false)} className="text-foreground-700 text-sm font-medium py-2.5 border-b border-background-100">
            <i className="ri-grid-line mr-2"></i>Categories
          </a>

          <div className="py-2">
            <p className="text-foreground-400 text-[11px] tracking-wider uppercase font-medium mb-2 px-1">Quick Category Links</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="text-foreground-600 text-xs py-2 px-2 rounded-lg hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap truncate"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <a href="#guides" onClick={() => setMobileOpen(false)} className="text-foreground-700 text-sm font-medium py-2.5 border-b border-background-100">
            <i className="ri-article-line mr-2"></i>Guides
          </a>
          <Link to="/spreadsheet-beginners-guide" onClick={() => setMobileOpen(false)} className="text-foreground-700 text-sm font-medium py-2.5 border-b border-background-100">
            <i className="ri-shopping-bag-line mr-2"></i>Shopping Guide
          </Link>
          <Link to="/best-gtbuy-spreadsheet-sources" onClick={() => setMobileOpen(false)} className="text-foreground-700 text-sm font-medium py-2.5 border-b border-background-100">
            <i className="ri-star-line mr-2"></i>Best Sources
          </Link>
          <Link to="/spreadsheet-comparison" onClick={() => setMobileOpen(false)} className="text-foreground-700 text-sm font-medium py-2.5 border-b border-background-100">
            <i className="ri-scales-line mr-2"></i>Platform Comparison
          </Link>
          <Link to="/spreadsheet-safety" onClick={() => setMobileOpen(false)} className="text-foreground-700 text-sm font-medium py-2.5 border-b border-background-100">
            <i className="ri-shield-check-line mr-2"></i>Safety Guide
          </Link>
          <a href="#about" onClick={() => setMobileOpen(false)} className="text-foreground-700 text-sm font-medium py-2.5 border-b border-background-100">
            <i className="ri-information-line mr-2"></i>About
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-5 py-3 rounded-full text-sm font-medium whitespace-nowrap mt-3"
          >
            Main Store
            <span className="w-5 h-5 flex items-center justify-center bg-background-50/20 rounded-full">
              <i className="ri-arrow-right-up-line text-xs"></i>
            </span>
          </a>
        </div>
      )}
    </nav>
  );
}